import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, finalize, forkJoin, Observable, of } from 'rxjs';
import { FriendRequestDto } from 'src/app/data-transfer/entities/friend-request-dto';
import { FriendRequestResultStatus } from 'src/app/data-transfer/entities/friend-request-result-status';
import { LinkedOrganizationDto } from 'src/app/data-transfer/entities/linked-organization-dto';
import { FriendRequestApiService } from 'src/app/data-transfer/services/friend-request-api-service';
import { LinkedOrganizationApiService } from 'src/app/data-transfer/services/linked-organization-api-service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import { SimpleConfirmDialogComponent } from 'src/app/components/dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { getApiErrorMessage } from '../admin-api-error';

const MIN_INVITE_CODE_LENGTH = 4;

@Component({
  selector: 'app-connected-organizations',
  templateUrl: './connected-organizations.component.html',
  styleUrl: './connected-organizations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectedOrganizationsComponent implements OnInit {
  private readonly friendRequestApiService = inject(FriendRequestApiService);
  private readonly linkedOrganizationApiService = inject(LinkedOrganizationApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly organizationName = input.required<string>();

  protected readonly inviteCodeControl = new FormControl<string>('', { nonNullable: true });
  private readonly inviteCode = toSignal(this.inviteCodeControl.valueChanges, { initialValue: '' });
  protected readonly expanded = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly loadFailed = signal(false);
  protected readonly isSendingInvite = signal(false);
  protected readonly organizationCode = signal('');
  private readonly pendingInvites = signal<FriendRequestDto[]>([]);
  protected readonly connectedOrganizations = signal<LinkedOrganizationDto[]>([]);
  private readonly processingRequestIds = signal<ReadonlySet<number>>(new Set());

  protected readonly incomingRequests = computed(() => this.pendingInvites().filter(request => request.receiverOrganizationName === this.organizationName()));
  protected readonly outgoingRequests = computed(() => this.pendingInvites().filter(request => request.senderOrganizationName === this.organizationName()));
  protected readonly canSendInvite = computed(() => this.inviteCode().trim().length >= MIN_INVITE_CODE_LENGTH && !this.isSendingInvite());
  protected readonly isEmpty = computed(
    () => !this.isLoading() && !this.loadFailed() && this.incomingRequests().length === 0 && this.outgoingRequests().length === 0 && this.connectedOrganizations().length === 0
  );

  ngOnInit(): void {
    this.isLoading.set(true);
    forkJoin({
      inviteCode: this.withFallback(this.friendRequestApiService.getInviteCode(true), null),
      pendingInvites: this.withFallback(this.friendRequestApiService.getPendingInvites(true), [] as FriendRequestDto[]),
      linkedOrganizations: this.withFallback(this.linkedOrganizationApiService.getLinkedOrganizations(true), [] as LinkedOrganizationDto[]),
    })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ inviteCode, pendingInvites, linkedOrganizations }) => {
        this.organizationCode.set(inviteCode?.inviteCode ?? '');
        this.pendingInvites.set(pendingInvites);
        this.connectedOrganizations.set(linkedOrganizations);
      });
  }

  private withFallback<T>(request: Observable<T>, fallback: T): Observable<T> {
    return request.pipe(
      catchError(error => {
        this.loadFailed.set(true);
        this.snackbarService.showError(getApiErrorMessage(error));
        return of(fallback);
      })
    );
  }

  protected isRequestProcessing(requestId: number): boolean {
    return this.processingRequestIds().has(requestId);
  }

  protected unfriendOrganization(organization: LinkedOrganizationDto): void {
    const dialogData: SimpleDialogData = {
      title: this.translocoService.translate('organizationAdmin.friendRequests.unfriendConfirm.title', { name: organization.name }),
      messages: [this.translocoService.translate('organizationAdmin.friendRequests.unfriendConfirm.message')],
      icon: 'link_off',
    };

    this.dialog
      .open(SimpleConfirmDialogComponent, getDialogConfig(dialogData, '480px'))
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;

        this.linkedOrganizationApiService
          .disconnect(organization.id, true)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.reload();
              this.snackbarService.showInfo(this.translocoService.translate('organizationAdmin.friendRequests.unfriendSuccess', { name: organization.name }), 3000);
            },
            error: error => this.snackbarService.showError(getApiErrorMessage(error)),
          });
      });
  }

  protected sendFriendRequest(): void {
    if (!this.canSendInvite()) return;

    this.isSendingInvite.set(true);
    this.friendRequestApiService
      .sendFriendInvite({ inviteCode: this.inviteCodeControl.value.trim() }, true)
      .pipe(
        finalize(() => this.isSendingInvite.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          const messageKey =
            response.status === FriendRequestResultStatus.Accepted
              ? 'friendRequests.addInviteDialog.inviteAcceptedMessage'
              : 'friendRequests.addInviteDialog.inviteSentSuccessMessage';
          this.snackbarService.showInfo(this.translocoService.translate(messageKey), 3000);
          this.inviteCodeControl.reset('');
          this.reload();
        },
        error: error => this.snackbarService.showError(this.getErrorMessageKey(error?.error?.status)),
      });
  }

  protected acceptRequest(request: FriendRequestDto): void {
    this.processRequest(request, this.friendRequestApiService.acceptFriendRequest(request.id, true));
  }

  protected rejectRequest(request: FriendRequestDto): void {
    this.processRequest(request, this.friendRequestApiService.rejectFriendRequest(request.id, true));
  }

  private processRequest(request: FriendRequestDto, call: Observable<void>): void {
    if (this.isRequestProcessing(request.id)) return;
    this.setProcessing(request.id, true);

    call
      .pipe(
        finalize(() => this.setProcessing(request.id, false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.reload(),
        error: error => this.snackbarService.showError(getApiErrorMessage(error)),
      });
  }

  private setProcessing(requestId: number, isProcessing: boolean): void {
    this.processingRequestIds.update(ids => {
      const next = new Set(ids);
      if (isProcessing) {
        next.add(requestId);
      } else {
        next.delete(requestId);
      }
      return next;
    });
  }

  private reload(): void {
    this.isLoading.set(true);
    this.loadFailed.set(false);
    forkJoin({
      pendingInvites: this.withFallback(this.friendRequestApiService.getPendingInvites(true), [] as FriendRequestDto[]),
      linkedOrganizations: this.withFallback(this.linkedOrganizationApiService.getLinkedOrganizations(true), [] as LinkedOrganizationDto[]),
    })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ pendingInvites, linkedOrganizations }) => {
        this.pendingInvites.set(pendingInvites);
        this.connectedOrganizations.set(linkedOrganizations);
        this.friendRequestApiService.reloadPendingInvites(true);
      });
  }

  private getErrorMessageKey(status?: FriendRequestResultStatus): string {
    switch (status) {
      case FriendRequestResultStatus.InvalidInviteCode:
        return 'friendRequests.addInviteDialog.invalidInviteCodeError';
      case FriendRequestResultStatus.OrganizationNotFound:
        return 'friendRequests.addInviteDialog.organizationNotFoundError';
      case FriendRequestResultStatus.SelfRequest:
        return 'friendRequests.addInviteDialog.selfRequestError';
      case FriendRequestResultStatus.AlreadyConnected:
        return 'friendRequests.addInviteDialog.alreadyConnectedError';
      case FriendRequestResultStatus.RequestAlreadyExists:
        return 'friendRequests.addInviteDialog.requestAlreadyExistsError';
      default:
        return 'friendRequests.addInviteDialog.unexpectedError';
    }
  }
}
